import { useEffect, useState } from "react";
import { ArrowRight, GitMerge, MagicWand, Prohibit, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { notifyContentUpdated, subscribeContentUpdates } from "../../lib/contentSync";

function statusLabel(status) {
    if (status === "pending") return "Menunggu ACC";
    if (status === "rejected") return "Ditolak";
    return "Tampil";
}

export default function EventMergeManager({ onMerged }) {
    const [suggestions, setSuggestions] = useState([]);
    const [exclusions, setExclusions] = useState([]);
    const [choices, setChoices] = useState({});
    const [loading, setLoading] = useState(true);
    const [merging, setMerging] = useState("");
    const [dismissing, setDismissing] = useState("");
    const [restoring, setRestoring] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const [suggestionsRes, exclusionsRes] = await Promise.all([
                api.get("/admin/event-suggestions"),
                api.get("/admin/event-suggestions/exclusions"),
            ]);
            setSuggestions(suggestionsRes.data);
            setExclusions(exclusionsRes.data);
            setChoices(Object.fromEntries(suggestionsRes.data.map((item) => [item.id, item.target_event])));
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        return subscribeContentUpdates(load);
    }, []);

    const merge = async (suggestion) => {
        const target = choices[suggestion.id] || suggestion.target_event;
        const source = target === suggestion.source_event ? suggestion.target_event : suggestion.source_event;
        if (!window.confirm(`Gabungkan ${suggestion.source_count} prestasi dari "${source}" ke "${target}"? Semua status, termasuk yang menunggu ACC, akan ikut berubah.`)) return;
        setMerging(suggestion.id);
        try {
            const { data } = await api.post("/admin/events/merge", { source_event: source, target_event: target });
            toast.success(`${data.merged_count} prestasi digabung ke ${data.target_event}`);
            notifyContentUpdated();
            await load();
            if (onMerged) onMerged();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setMerging("");
        }
    };

    const dismiss = async (suggestion) => {
        if (!window.confirm(`Tandai "${suggestion.source_event}" dan "${suggestion.target_event}" sebagai event yang berbeda? Saran ini tidak akan muncul lagi.`)) return;
        setDismissing(suggestion.id);
        try {
            await api.post("/admin/event-suggestions/dismiss", {
                source_event: suggestion.source_event,
                target_event: suggestion.target_event,
            });
            toast.success("Pasangan event ditandai berbeda");
            await load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setDismissing("");
        }
    };

    const restore = async (exclusion) => {
        if (!window.confirm(`Batalkan tanda berbeda untuk "${exclusion.source_event}" dan "${exclusion.target_event}"?`)) return;
        setRestoring(exclusion.id);
        try {
            await api.delete(`/admin/event-suggestions/exclusions/${exclusion.id}`);
            toast.success("Saran penggabungan ditampilkan kembali");
            await load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setRestoring("");
        }
    };

    return (
        <section className="mb-8 border border-racing-line bg-white" data-testid="event-merge-manager">
            <div className="p-5 border-b border-racing-line flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                    <h3 className="flex items-center gap-2 font-display uppercase text-xl"><GitMerge size={20} className="text-racing-red" /> Saran Penggabungan Event</h3>
                    <p className="text-sm text-racing-muted mt-1">Sistem mencari nama event yang kemungkinan sama. Jika ternyata berbeda, tandai agar saran tidak muncul lagi.</p>
                </div>
                <button type="button" onClick={load} data-testid="event-suggestions-refresh" className="border border-racing-line px-4 py-2 text-xs uppercase tracking-[0.2em] text-racing-muted hover:text-racing-red hover:border-racing-red transition-colors">
                    Muat Ulang
                </button>
            </div>

            {loading ? (
                <p className="p-6 text-sm text-racing-muted" data-testid="event-suggestions-loading">Memeriksa kemiripan nama event...</p>
            ) : suggestions.length === 0 ? (
                <p className="p-6 text-sm text-racing-muted" data-testid="event-suggestions-empty">Tidak ada saran baru. Data event sudah rapi atau pasangan mirip sudah ditandai berbeda.</p>
            ) : (
                <div className="divide-y divide-racing-line" data-testid="event-suggestions-list">
                    {suggestions.map((suggestion, index) => (
                        <div key={suggestion.id} className="p-5 grid lg:grid-cols-[1fr_auto_auto_1fr_auto_auto] gap-4 items-center" data-testid={`event-suggestion-${index}`}>
                            <div className="min-w-0">
                                <p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-1">Nama Alternatif</p>
                                <p className="font-medium text-neutral-900 break-words" data-testid={`event-suggestion-source-${index}`}>{suggestion.source_event}</p>
                                <p className="text-xs text-racing-muted mt-1">{suggestion.source_count} prestasi • {Object.entries(suggestion.source_statuses).map(([status, count]) => `${statusLabel(status)} ${count}`).join(" • ")}</p>
                            </div>
                            <span className="hidden lg:inline-flex w-10 h-10 items-center justify-center border border-racing-line text-racing-red"><ArrowRight size={18} /></span>
                            <div className="text-center">
                                <span className="inline-flex items-center gap-1 border border-racing-gold bg-racing-gold/10 text-racing-golddeep px-3 py-1.5 text-[10px] uppercase tracking-[0.15em]" data-testid={`event-suggestion-score-${index}`}>
                                    <MagicWand size={14} /> Mirip {Math.round(suggestion.similarity * 100)}%
                                </span>
                                <p className="mt-1 text-[10px] text-racing-muted">Tahun: {suggestion.years.join(", ") || "—"}</p>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Nama Event Utama</label>
                                <select
                                    value={choices[suggestion.id] || suggestion.target_event}
                                    onChange={(e) => setChoices({ ...choices, [suggestion.id]: e.target.value })}
                                    data-testid={`event-merge-target-${index}`}
                                    className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red"
                                >
                                    <option value={suggestion.target_event}>{suggestion.target_event}</option>
                                    <option value={suggestion.source_event}>{suggestion.source_event}</option>
                                </select>
                            </div>
                            <button
                                type="button"
                                onClick={() => merge(suggestion)}
                                disabled={merging === suggestion.id || dismissing === suggestion.id}
                                data-testid={`event-merge-button-${index}`}
                                className="bg-racing-red text-white px-4 py-3 text-xs uppercase tracking-[0.2em] hover:bg-racing-redhover disabled:opacity-50 transition-colors"
                            >
                                {merging === suggestion.id ? "Menggabung..." : "Gabungkan"}
                            </button>
                            <button
                                type="button"
                                onClick={() => dismiss(suggestion)}
                                disabled={merging === suggestion.id || dismissing === suggestion.id}
                                data-testid={`event-dismiss-button-${index}`}
                                className="inline-flex items-center justify-center gap-2 border border-racing-line text-racing-muted px-4 py-3 text-xs uppercase tracking-[0.2em] hover:text-racing-red hover:border-racing-red disabled:opacity-50 transition-colors"
                            >
                                <Prohibit size={16} /> {dismissing === suggestion.id ? "Menyimpan..." : "Ini Event Berbeda"}
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {!loading && exclusions.length > 0 && (
                <div className="border-t border-racing-line bg-racing-surface/50" data-testid="event-exclusions-section">
                    <div className="px-5 py-4 border-b border-racing-line">
                        <h4 className="font-display uppercase text-lg">Event yang Ditandai Berbeda</h4>
                        <p className="text-xs text-racing-muted mt-1">Pasangan ini tidak akan muncul lagi sebagai saran. Klik Batalkan jika salah menandai.</p>
                    </div>
                    <div className="divide-y divide-racing-line">
                        {exclusions.map((exclusion, index) => (
                            <div key={exclusion.id} className="px-5 py-4 flex flex-col lg:flex-row lg:items-center gap-4" data-testid={`event-exclusion-${index}`}>
                                <div className="flex-1 min-w-0 text-sm">
                                    <span className="font-medium text-neutral-900">{exclusion.source_event}</span>
                                    <span className="mx-2 text-racing-muted">≠</span>
                                    <span className="font-medium text-neutral-900">{exclusion.target_event}</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => restore(exclusion)}
                                    disabled={restoring === exclusion.id}
                                    data-testid={`event-exclusion-restore-${index}`}
                                    className="inline-flex items-center justify-center gap-2 border border-racing-line px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-racing-muted hover:text-racing-red hover:border-racing-red disabled:opacity-50 transition-colors"
                                >
                                    <ArrowCounterClockwise size={15} /> {restoring === exclusion.id ? "Membuka..." : "Batalkan"}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
}
